import { useState } from "react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePickerField } from "@/components/DatePickerField";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";

const fmt = (d) => (d ? new Date(`${d.slice(0, 10)}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");

export const TrackingReviewDialog = ({ container, reported, override, flags, onDone, compact }) => {
  const [open, setOpen] = useState(false);
  const [decision, setDecision] = useState(override ? "release_override" : "accept_carrier");
  const [value, setValue] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const options = [
    reported && { v: "accept_carrier", l: `Accept carrier ETA ${fmt(reported.value)}`, d: "Replaces the Admin Miami port ETA with the carrier's value and releases any override." },
    container.eta_carrier && { v: "keep_manual", l: `Keep Admin ETA ${fmt(container.eta_carrier)}`, d: "Saves a confirmed override. Carrier updates will be recorded but never replace it." },
    { v: "override", l: "Enter a different ETA", d: "Saves a confirmed override with your reason." },
    override && { v: "release_override", l: "Release the confirmed override", d: "Carrier updates may adjust the ETA again (within the threshold)." },
    flags.length > 0 && { v: "dismiss", l: "Dismiss warnings only", d: "Resolves the flags without changing any ETA." },
  ].filter(Boolean);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/containers/${container.id}/tracking/review`, { decision, value: decision === "override" ? value : undefined, reason, flag_ids: flags.map((f) => f.id) });
      toast.success("Review saved — audited");
      setOpen(false);
      setReason("");
      onDone();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size={compact ? "sm" : "default"} variant={compact ? "outline" : "default"} data-testid="tracking-review-button" className={compact ? "h-8 border-limestone text-xs" : "h-10 bg-charcoal text-white hover:bg-charcoal/90"}>
          {compact ? "Revise or release" : "Review"}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-border bg-white sm:max-w-md" data-testid="tracking-review-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-medium text-charcoal">Review ETA information</DialogTitle>
          <DialogDescription className="text-sm text-warmgrey">
            Carrier ETA reported: <strong>{reported ? fmt(reported.value) : "none"}</strong>{reported ? ` (${reported.source})` : ""} · Admin Miami port ETA: <strong>{fmt(container.eta_carrier)}</strong>. An earlier ETA is not automatically wrong — decide deliberately. Every decision is audited with actor, time and reason.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="mt-2 space-y-4" data-testid="tracking-review-form">
          <div className="space-y-2">
            {options.map((o) => (
              <label key={o.v} className={`flex cursor-pointer gap-3 rounded-md border p-3 ${decision === o.v ? "border-terracotta bg-terracotta/5" : "border-border"}`} data-testid={`review-option-${o.v}`}>
                <input type="radio" name="decision" value={o.v} checked={decision === o.v} onChange={() => setDecision(o.v)} className="mt-1 accent-[#B86F54]" />
                <span><span className="block text-sm font-medium text-charcoal">{o.l}</span><span className="block text-xs text-warmgrey">{o.d}</span></span>
              </label>
            ))}
          </div>
          {decision === "override" && (
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Override Miami port ETA *</Label>
              <DatePickerField required value={value} onChange={setValue} testId="review-override-date" />
            </div>
          )}
          {decision !== "accept_carrier" && (
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Reason *</Label>
              <Input required minLength={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why is this the right decision?" data-testid="review-reason-input" className="h-11 bg-white" />
            </div>
          )}
          <Button type="submit" disabled={saving} data-testid="review-submit" className="h-11 w-full bg-terracotta text-white hover:bg-terracotta-hover">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save decision (audited)"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
